import * as z from "zod";
import { CartSchema, CartItemSchema } from "./carts";
import { AddressSchema, createAddressSchema } from "../business/address";
import { CompanyFreightSchema } from "../business/freight";
import { UserSessionSchema } from "../core/auth";

// Import from checkout.ts - Moving checkout validation to orders.ts
export const shippingRateSchema = z.object({
  id: z.string(),
  label: z.string(),
  price: z.number().nonnegative(),
  estimatedDays: z
    .object({
      earliest: z.number(),
      latest: z.number(),
    })
    .optional(),
});

export const DeliveryServiceSchema = z.object({
  id: z.string(),
  name: z.string(),
  carrier: z.string().optional(),
  price: z.number().nonnegative().optional(),
  estimatedDays: z
    .object({
      earliest: z.number().optional(),
      latest: z.number().optional(),
    })
    .optional(),
  shippingServiceId: z.string().optional(),
});

export const OrderStatusEnum = z.enum([
  // Initial Order Stages
  "pending-payment",
  "verifying-purchase-order",
  "purchase-order-confirmed",
  "invalid-purchase-order",
  "payment-confirmed",
  "processing",
  // Engineering & Planning Stages
  "engineering-review",
  "design-approval",
  "materials-review",
  // Production Planning Stages
  "queued-production",
  "materials-allocated",
  "in-production",
  "production-hold",
  "qa-inspection",
  "qa-failed",
  "rework",
  // Post-Production Stages
  "production-completed",
  "final-inspection",
  "packaging",
  // Shipping Stages
  "ready-for-shipment",
  "awaiting-pickup",
  "in-transit",
  "delivered",
  // Problem States
  "on-hold",
  "cancelled",
  "returned",
  "refunded",
]);

export const PaymentStatusEnum = z.enum([
  "pending",
  "processing",
  "succeeded",
  "failed",
  "refunded",
]);

export const OrderStatusHistorySchema = z.object({
  id: z.string().uuid(),
  order_id: z.string().uuid(),
  previous_status: OrderStatusEnum.optional(),
  new_status: OrderStatusEnum,
  notes: z.string().optional().nullable(),
  changed_by: z.string().uuid(),
  created: z
    .date()
    .optional()
    .default(() => new Date()),
});

export type OrderEditableField =
  | "status"
  | "priority"
  | "payment_status"
  | "shipping_method"
  | "shipping_option"
  | "shipping_price"
  | "tracking_number"
  | "notes"
  | "internal_notes"
  | "engineering_notes"
  | "quality_notes"
  | "production_notes"
  | "estimated_completion_date"
  | "freight_account_details"
  | "requires_shipping_contact"
  | "purchase_order_number"
  | "purchase_order_url";

export const OrderSchema = z.object({
  id: z.string().uuid(),
  order_number: z.string().min(1).max(50),
  user_id: z.string().uuid(),
  cart_id: z.string().uuid().optional().nullable(),
  quote_number: z.string().optional().nullable(),
  status: OrderStatusEnum.default("pending-payment"),
  status_history: z.array(OrderStatusHistorySchema).optional(),

  // Timestamps
  payment_confirmed_at: z.date().optional().nullable(),
  engineering_review_at: z.date().optional().nullable(),
  design_approval_at: z.date().optional().nullable(),
  production_started_at: z.date().optional().nullable(),
  qa_inspection_at: z.date().optional().nullable(),
  production_completed_at: z.date().optional().nullable(),
  shipped_at: z.date().optional().nullable(),
  delivered_at: z.date().optional().nullable(),

  // Manufacturing fields
  engineering_notes: z.string().optional().nullable(),
  quality_notes: z.string().optional().nullable(),
  production_notes: z.string().optional().nullable(),
  estimated_completion_date: z.date().optional().nullable(),
  priority: z.boolean().default(false),

  // Shipping & Customer Info
  shipping_method: z.string().max(100).optional().nullable(),
  shipping_option: z.string().max(100).optional().nullable(),
  tracking_number: z.string().max(100).optional().nullable(),
  shipping_address_id: z.string().uuid(),
  billing_address_id: z.string().uuid(),
  shipping_address: AddressSchema.optional(),
  billing_address: AddressSchema.optional(),
  notes: z.string().optional().nullable(),
  internal_notes: z.string().optional().nullable(),

  // Custom shipping options
  freight_account_details: z.string().optional().nullable(),
  requires_shipping_contact: z.boolean().default(false),

  // Payment Info - Price breakdown fields
  total: z.number().positive().multipleOf(0.01),
  subtotal: z.number().nonnegative().multipleOf(0.01).optional().nullable(),
  shipping_price: z
    .number()
    .nonnegative()
    .multipleOf(0.01)
    .optional()
    .nullable(),
  gst_amount: z.number().nonnegative().multipleOf(0.01).optional().nullable(),
  card_surcharge: z
    .number()
    .nonnegative()
    .multipleOf(0.01)
    .optional()
    .nullable(),
  payment_status: PaymentStatusEnum.default("pending"),
  stripe_session_id: z.string().max(255).optional().nullable(),
  stripe_payment_intent_id: z.string().max(255).optional().nullable(),
  purchase_order_number: z.string().max(100).optional().nullable(),
  purchase_order_url: z.string().max(255).optional().nullable(),

  // Audit timestamps
  created: z.date().optional(),
  updated: z.date().optional(),

  // Relations
  cart: CartSchema.optional(),
  user: UserSessionSchema.optional(),
});

export const CreateOrderInputSchema = z.object({
  cart: CartSchema,
  user_data: UserSessionSchema,
  shippingAddress: createAddressSchema,
  billingAddress: createAddressSchema,
  shipping: DeliveryServiceSchema,
  notes: z.string().optional().nullable(),
  freight: CompanyFreightSchema.optional().nullable(),
  total: z.number().positive().multipleOf(0.01),
  stripe_session_id: z.string().max(255).optional().nullable(),
  stripe_payment_intent_id: z.string().max(255).optional().nullable(),
  purchase_order_number: z.string().max(100).optional().nullable(),
  purchase_order_url: z.string().max(255).optional().nullable(),
});

export const UpdateOrderInputSchema = z.object({
  id: z.string().uuid(),
  user_data: UserSessionSchema,
  send_client_email: z.boolean().optional().default(false),
  data: z
    .object({
      status: OrderStatusEnum.optional(),
      priority: z.boolean().optional(),
      payment_status: PaymentStatusEnum.optional(),
      shipping_method: z.string().max(100).optional().nullable(),
      shipping_option: z.string().max(100).optional().nullable(),
      shipping_price: z
        .number()
        .positive()
        .multipleOf(0.01)
        .optional()
        .nullable(),
      tracking_number: z.string().max(100).optional().nullable(),
      notes: z.string().optional().nullable(),
      internal_notes: z.string().optional().nullable(),
      engineering_notes: z.string().optional().nullable(),
      quality_notes: z.string().optional().nullable(),
      production_notes: z.string().optional().nullable(),
      estimated_completion_date: z.date().optional().nullable(),
      freight_account_details: z.string().optional().nullable(),
      requires_shipping_contact: z.boolean().optional(),
      purchase_order_number: z.string().max(100).optional().nullable(),
      purchase_order_url: z.string().max(255).optional().nullable(),
    })
    .partial()
    .refine((data) => Object.keys(data).length > 0, {
      message: "At least one field must be provided for update.",
    }),
});

// Checkout form validation schemas (Moved from checkout.ts)
export const checkoutSchema = z.object({
  // Cart data - make optional since they might not be included in the form data
  cartItems: z.array(CartItemSchema).optional(),
  shippingRate: shippingRateSchema.optional(),

  // Order identifier
  quoteNumber: z.string(),

  // User info
  userId: z.string().uuid().optional(),
  email: z.string().email({ message: "Please enter a valid email address." }),
  firstName: z.string().min(1, { message: "First name is required." }),
  lastName: z.string().min(1, { message: "Last name is required." }),
  company: z.string().optional(),
  phone: z
    .string()
    .min(1, { message: "Phone number is required." })
    .regex(/^(\+?\d{1,3}[-.\s]?)?(\(?\d{3}\)?[-.\s]?)?(\d{3})[-.\s]?(\d{4})$/, {
      message: "Please enter a valid phone number.",
    }),
  notes: z.string().optional().nullable(),

  // Saved address IDs
  addressId: z.string().optional(),

  // Shipping address - all optional by default, conditionally required
  address1: z.string().optional(),
  address2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postal_code: z.string().optional(),
  country: z.string().optional(),

  // Billing address - all optional by default, conditionally required
  useSameAddress: z.boolean().default(true),
  billingAddress1: z.string().optional(),
  billingAddress2: z.string().optional(),
  billingCity: z.string().optional(),
  billingState: z.string().optional(),
  billingPostalCode: z.string().optional(),
  billingCountry: z.string().optional(),

  // Delivery options
  deliveryServiceId: z
    .string()
    .min(1, { message: "Please select a delivery method." }),
  freightAccountId: z.string().optional(),

  // Payment
  isPurchaseOrder: z.boolean().default(false),
  storePaymentMethod: z.boolean().optional(),
  stripeCustomerId: z.string().optional(),
  purchaseOrderNumber: z.string().optional(),
  purchaseOrderFile: z.any().optional(),
  stripePaymentIntentId: z.string().optional(),
});

// Refined schema with conditional validation
export const refinedCheckoutSchema = checkoutSchema.superRefine((data, ctx) => {
  // Shipping address is only required when delivery method is standard delivery ("deliver")
  if (data.deliveryServiceId === "deliver" && !data.addressId) {
    if (!data.address1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Address is required for standard delivery",
        path: ["address1"],
      });
    }
    if (!data.city) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "City is required for standard delivery",
        path: ["city"],
      });
    }
    if (!data.state) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "State is required for standard delivery",
        path: ["state"],
      });
    }
    if (!data.postal_code) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Postal code is required for standard delivery",
        path: ["postal_code"],
      });
    }
    if (!data.country) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Country is required for standard delivery",
        path: ["country"],
      });
    }
  }

  // Billing address is only required when not using purchase order and not same as shipping
  if (!data.isPurchaseOrder && !data.useSameAddress) {
    if (!data.billingAddress1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Billing address is required for credit card payment",
        path: ["billingAddress1"],
      });
    }
    if (!data.billingCity) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Billing city is required for credit card payment",
        path: ["billingCity"],
      });
    }
    if (!data.billingState) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Billing state is required for credit card payment",
        path: ["billingState"],
      });
    }
    if (!data.billingPostalCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Billing postal code is required for credit card payment",
        path: ["billingPostalCode"],
      });
    }
    if (!data.billingCountry) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Billing country is required for credit card payment",
        path: ["billingCountry"],
      });
    }
  }

  // If using freight account, require freightAccountId
  if (
    data.deliveryServiceId === "own-freight-account" &&
    !data.freightAccountId
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Freight account is required when using your own freight",
      path: ["freightAccountId"],
    });
  }

  // If using purchase order, require purchaseOrderFile
  if (data.isPurchaseOrder && !data.purchaseOrderFile) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Purchase order document is required",
      path: ["purchaseOrderFile"],
    });
  }

  // If using purchase order, require purchaseOrderNumber
  if (data.isPurchaseOrder && !data.purchaseOrderNumber) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Purchase order number is required",
      path: ["purchaseOrderNumber"],
    });
  }
});

// Export types
export type CheckoutFormValues = z.infer<typeof checkoutSchema>;
export type ShippingRate = z.infer<typeof shippingRateSchema>;

// Schema for delivery services API requests
export const GetDeliveryServicesSchema = z.object({
  addressData: z
    .object({
      street: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      postCode: z.string().optional(),
      countryCode: z.string().optional(),
    })
    .optional(),
  cartItems: z.array(CartItemSchema).optional(),
});
